/**
 * Maintenance Hub.
 *
 * Combines work orders, recurring work order templates, and barcode
 * scanning into a single tabbed page. Voice WO is accessible via a
 * header button. Everything related to maintenance operations.
 */

import { useState } from "react";
import { Wrench, RefreshCw, ScanLine, Mic, Building2 } from "lucide-react";
import HubTabs from "@/components/ui/HubTabs";
import { useTabState } from "@/hooks/useTabState";
import Button from "@/components/ui/Button";
import WorkOrdersPage from "@/pages/WorkOrdersPage";
import RecurringWOsPage from "@/pages/RecurringWOsPage";
import ScanPage from "@/pages/ScanPage";
import VoiceWOPage from "@/pages/VoiceWOPage";
import ServiceProvidersPage from "@/pages/ServiceProvidersPage";

const tabs = [
  { key: "work-orders", label: "Work Orders", icon: Wrench },
  { key: "providers", label: "Service Providers", icon: Building2 },
  { key: "recurring", label: "Recurring", icon: RefreshCw },
  { key: "scan", label: "Scan", icon: ScanLine },
];

function MaintenanceHub() {
  const [activeTab, setActiveTab] = useTabState("tab", "work-orders");
  const [showVoice, setShowVoice] = useState(false);

  if (showVoice) {
    return (
      <div className="page-wide space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-[var(--text-primary)]">
              Maintenance
            </h2>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Work orders, recurring schedules, and scanning.
            </p>
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setShowVoice(false)}
          >
            ← Back to Maintenance
          </Button>
        </div>
        <VoiceWOPage />
      </div>
    );
  }

  return (
    <div className="page-wide space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[var(--text-primary)]">
            Maintenance
          </h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Work orders, recurring schedules, and scanning.
          </p>
        </div>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => setShowVoice(true)}
        >
          <Mic className="h-4 w-4" /> Voice WO
        </Button>
      </div>

      <HubTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === "work-orders" && <WorkOrdersPage />}
      {activeTab === "providers" && <ServiceProvidersPage />}
      {activeTab === "recurring" && <RecurringWOsPage />}
      {activeTab === "scan" && <ScanPage />}
    </div>
  );
}

export default MaintenanceHub;
